<template>
  <div id="app">
    <!-- 一级路由出口 -->
    <router-view />

    <!-- 客服新消息提示条：点击跳转到「发来消息的客服」聊天页 -->
    <div v-if="chatNotify" class="chat-notify" @click="goChat">
      <van-icon name="service-o" />
      <span class="label">客服新消息</span>
      <span class="preview">
        <template v-if="chatNotifyTarget.shopName">{{ chatNotifyTarget.shopName }}：</template>{{ chatNotify }}
      </span>
      <span class="close" @click.stop="chatNotify = ''">✕</span>
    </div>
  </div>
</template>

<script>
import { getChatSessionsApi, getChatMessagesApi } from '@/api/chat'
import { getChatNotifiedMsgId, setChatNotifiedMsgId } from '@/utils/chat'
import chatSocket from '@/utils/ws'

export default {
  name: 'App',
  data () {
    return {
      chatNotify: '', // 客服新消息预览（非空时展示提示条）
      // 新消息来源客服（用于点击提示条跳转到「对应客服」的聊天页）
      chatNotifyTarget: { merchantId: 0, shopName: '' },
      chatPollTimer: null,
      chatNotifyTimer: null,
      chatNotifierReady: false, // 首次连接只建立已读基线，避免对历史消息误提醒
      wsReady: false
    }
  },
  computed: {
    isLogin () {
      return !!this.$store.getters.token
    },
    onChatPage () {
      return this.$route.path === '/chat'
    },
    // 当前正在聊天的客服渠道（不在聊天页时为 null）
    currentChatMerchantId () {
      if (!this.onChatPage) return null
      return Number(this.$route.query.merchantId || 0)
    }
  },
  watch: {
    // 登录切换时重置已读基线，避免跨账号串号
    isLogin (val) {
      if (val) {
        this.chatNotifierReady = false
        this.bindChatSocket()
        chatSocket.connect('user')
      } else {
        this.wsReady = false
        chatSocket.disconnect()
        this.unbindChatSocket()
      }
      this.startChatPolling()
    },
    // 进入「该客服」聊天页时清除对应提示（切换商家时保留其它客服的提示）
    onChatPage (val) {
      if (val && this.chatNotify && this.isChattingWith(this.chatNotifyTarget.merchantId)) {
        this.chatNotify = ''
      }
    },
    // 在聊天页内切换不同客服时，同样清除对应提示
    currentChatMerchantId () {
      if (this.chatNotify && this.isChattingWith(this.chatNotifyTarget.merchantId)) {
        this.chatNotify = ''
      }
    }
  },
  mounted () {
    this.bindChatSocket()
    if (this.isLogin) {
      chatSocket.connect('user')
    }
    this.startChatPolling()
    this.buildBaseline()
  },
  beforeDestroy () {
    this.stopChatPolling()
    this.unbindChatSocket()
    if (this.chatNotifyTimer) clearTimeout(this.chatNotifyTimer)
  },
  methods: {
    // ==================== WebSocket 实时通知 ====================
    bindChatSocket () {
      this.unbindChatSocket()
      // 连接建立（含重连）：补建一次已读基线，避免把断线期间的历史客服消息当成新消息
      this._onWsOpen = () => {
        this.wsReady = true
        this.buildBaseline()
      }
      this._onWsClose = () => { this.wsReady = false }
      this._onWsError = () => { this.wsReady = false }
      this._onWsMessage = (payload) => {
        const msg = payload && payload.message
        if (!msg) return
        const msgId = Number(msg.id) || 0
        const session = (payload && payload.session) || {}
        // 消息来源客服渠道（官方自营店铺沿用官方客服渠道 0）
        const fromMerchantId = session.merchant_id !== undefined ? Number(session.merchant_id) : 0
        const fromShopName = session.shop_name || (fromMerchantId > 0 ? '商家客服' : '商城官方客服')
        // 仅提示客服（sender_type=2）发来的消息；正在与该客服聊天时不提示
        if (Number(msg.sender_type) === 2 && !this.isChattingWith(fromMerchantId)) {
          const preview = Number(msg.msg_type) === 2 ? '[图片]' : String(msg.content || '').slice(0, 30)
          if (preview) {
            this.showChatNotify(preview, { merchantId: fromMerchantId, shopName: fromShopName })
          }
        }
        if (msgId > getChatNotifiedMsgId()) setChatNotifiedMsgId(msgId)
      }
      this._onWsReady = () => { this.chatNotifierReady = true }

      chatSocket.on('open', this._onWsOpen)
      chatSocket.on('close', this._onWsClose)
      chatSocket.on('error', this._onWsError)
      chatSocket.on('message', this._onWsMessage)
      chatSocket.on('ready', this._onWsReady)
    },
    unbindChatSocket () {
      if (this._onWsOpen) chatSocket.off('open', this._onWsOpen)
      if (this._onWsClose) chatSocket.off('close', this._onWsClose)
      if (this._onWsError) chatSocket.off('error', this._onWsError)
      if (this._onWsMessage) chatSocket.off('message', this._onWsMessage)
      if (this._onWsReady) chatSocket.off('ready', this._onWsReady)
      this._onWsOpen = null
      this._onWsClose = null
      this._onWsError = null
      this._onWsMessage = null
      this._onWsReady = null
    },
    // 建立已读基线：把所有进行中会话的最新消息 id 记为已读，避免历史消息被当成新消息提示
    async buildBaseline () {
      if (!this.isLogin) return
      try {
        const { data } = await getChatSessionsApi()
        const list = (data && data.list) || []
        if (!list.length) {
          this.chatNotifierReady = true
          return
        }
        const lastId = getChatNotifiedMsgId()
        let maxId = lastId
        // 逐个会话（最多 5 个）取最新消息 id，保证「各个商家客服」都不会误提醒历史消息
        for (const session of list.slice(0, 5)) {
          const res = await getChatMessagesApi(session.id, lastId)
          const messages = (res.data && res.data.messages) || []
          for (const m of messages) {
            if (Number(m.id) > maxId) maxId = Number(m.id)
          }
        }
        if (maxId > lastId) setChatNotifiedMsgId(maxId)
        this.chatNotifierReady = true
      } catch (e) {
        // 静默失败，避免打扰用户
      }
    },
    // 兜底轮询：仅在 WebSocket 未连接时启用（间隔 15 秒）
    startChatPolling () {
      this.stopChatPolling()
      if (!this.isLogin) return
      this.chatPollTimer = setInterval(() => {
        if (chatSocket.isOpen) return
        this.pollNewChatMessages()
      }, 15000)
    },
    stopChatPolling () {
      if (this.chatPollTimer) {
        clearInterval(this.chatPollTimer)
        this.chatPollTimer = null
      }
    },
    // 轮询是否有客服发来的新消息（WebSocket 不可用时的兜底，覆盖各商家客服会话）
    async pollNewChatMessages () {
      if (!this.isLogin) return
      try {
        const { data } = await getChatSessionsApi()
        const list = (data && data.list) || []

        // 首次轮询：只建立已读基线，避免对已存在的历史消息误提醒
        if (!this.chatNotifierReady) {
          await this.buildBaseline()
          return
        }
        if (!list.length) return

        const lastId = getChatNotifiedMsgId()
        let maxId = lastId
        let preview = ''
        let target = { merchantId: 0, shopName: '' }

        // 逐个会话（最多 5 个）检查新消息，取最新一条「客服发来」的消息用于提示
        for (const session of list.slice(0, 5)) {
          const res = await getChatMessagesApi(session.id, lastId)
          const messages = (res.data && res.data.messages) || []
          for (const m of messages) {
            if (Number(m.id) > maxId) maxId = Number(m.id)
            // 仅提示客服（sender_type=2）发来的消息
            if (Number(m.sender_type) === 2) {
              preview = Number(m.msg_type) === 2 ? '[图片]' : String(m.content || '').slice(0, 30)
              target = {
                merchantId: Number(session.merchant_id || 0),
                shopName: session.shop_name || (Number(session.merchant_id) > 0 ? '商家客服' : '商城官方客服')
              }
            }
          }
        }
        if (maxId > lastId) setChatNotifiedMsgId(maxId)

        // 轮询请求在途时用户可能已进入该客服的聊天页，此时无需再弹提示
        if (preview && !this.isChattingWith(target.merchantId)) {
          this.showChatNotify(preview, target)
        }
      } catch (e) {
        // 静默失败，避免打扰用户
      }
    },
    // 当前是否正在与该客服（渠道）聊天：是则不重复弹新消息提示
    isChattingWith (merchantId) {
      return this.currentChatMerchantId !== null && Number(this.currentChatMerchantId) === Number(merchantId)
    },
    showChatNotify (preview, target) {
      this.chatNotify = preview
      if (target) {
        this.chatNotifyTarget = {
          merchantId: Number(target.merchantId) || 0,
          shopName: target.shopName || ''
        }
      }
      if (this.chatNotifyTimer) clearTimeout(this.chatNotifyTimer)
      this.chatNotifyTimer = setTimeout(() => {
        this.chatNotify = ''
      }, 8000)
    },
    // 点击提示条跳转到「发来消息的那个客服」的聊天页
    goChat () {
      const target = this.chatNotifyTarget || { merchantId: 0, shopName: '' }
      const merchantId = Number(target.merchantId) || 0
      const shopName = target.shopName || ''
      this.chatNotify = ''
      if (this.chatNotifyTimer) clearTimeout(this.chatNotifyTimer)
      // 已在该客服聊天页 → 无需跳转
      if (this.isChattingWith(merchantId)) {
        return
      }
      this.$router.push({
        path: '/chat',
        query: { merchantId: String(merchantId), shopName }
      })
    }
  }
}
</script>

<style lang="less">
.chat-notify {
  position: fixed;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3000;
  max-width: 92%;
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  border-radius: 22px;
  padding: 10px 16px;
  font-size: 13px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  cursor: pointer;

  .van-icon {
    font-size: 18px;
    margin-right: 8px;
    color: #ffd21e;
  }

  .label {
    flex-shrink: 0;
    color: #ffd21e;
    font-weight: 600;
    margin-right: 8px;
  }

  .preview {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 220px;
  }

  .close {
    margin-left: 10px;
    color: #bbb;
    font-size: 14px;
    padding: 4px;
  }
}
</style>

